import { Text, useComputedColorScheme } from '@mantine/core';
import { useAtom, useAtomValue, useSetAtom } from 'jotai';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  BrokerNerdsBarkerCoreEnumsMarketplace,
  BrokerNerdsBarkerCoreModelsAdministrativeTenantPricerSettings,
  BrokerNerdsBarkerCoreModelsPricingSectionRowFilter,
  getGetApiInventoryEventsEventIdMappingsQueryKey,
  getGetApiMarketplacesMarketplaceIdBoostsEnabledQueryKey,
  getGetApiMarketplacesMarketplaceIdEventsEventIdListingsQueryKey,
  getGetApiMarketplacesMarketplaceIdEventsEventIdSeatingChartQueryKey,
  getGetApiPricingSuggestionsQueryKey,
  postApiMarketplacesMarketplaceIdEventsEventIdBoost,
  useGetApiInventoryEventsEventIdMappings,
  useGetApiMarketplacesMarketplaceIdBoostsEnabled,
  useGetApiMarketplacesMarketplaceIdEventsEventId,
  useGetApiMarketplacesMarketplaceIdEventsEventIdListings,
  useGetApiMarketplacesMarketplaceIdEventsEventIdSeatingChart,
  useGetApiMarketplacesMarketplaceIdEventsEventIdSectionAliases,
  useGetApiPricingSuggestions,
} from '../../api';
import {
  eventMarketplaceDefaultsAtom,
  initialPageLoadAtom,
  ruleStateAtom,
  sectionSuggestionsAtom,
  selectedEventAtom,
  selectedListingRuleIdAtom,
  selectedMarketplaceEventAtom,
  selectedMarketplaceIdAtom,
  selectedSectionAtom,
  selectedTenantListingIdAtom,
  showEventMappingAtom,
} from '../../data/atoms';
import * as R from 'remeda';
import { useDidUpdate, useDisclosure, useToggle } from '@mantine/hooks';
import yasml from '@thirtytech/yasml';
import { queryClient } from '../../data/api-config';
import { useRuleState } from '../../data/Rule.state';
import { matchesRowFilter } from '../MarketListings/marketListingsFilter';
import { RecordSectionCounts, SectionCounts } from '../../types';
import { openConfirmModal } from '@mantine/modals';
import { useGlobalState } from '../../data/Global.state';
import { useEffectDeep } from '../../utils/use-memo-weak';
import { useFlag } from '@unleash/proxy-client-react';
import { useForm } from '@mantine/form';
import { parseEventIdFromUrl } from '../../utils/urls';

export const onAcceptSuggestions = new Event('onAcceptSuggestions');

function SeatingChartState({ isSeasonView }: { isSeasonView?: boolean }) {
  const computedColorScheme = useComputedColorScheme();
  const isDark = computedColorScheme === 'dark';
  const selectedSection = useAtomValue(selectedSectionAtom);
  const selectedEvent = useAtomValue(selectedEventAtom);
  const selectedTenantId = selectedEvent?.tenantId;
  const selectedTenantListingId = useAtomValue(selectedTenantListingIdAtom);
  const selectedListingRuleId = useAtomValue(selectedListingRuleIdAtom);
  const setSelectedMarketplaceId = useSetAtom(selectedMarketplaceIdAtom);
  const setSelectedMarketplaceEvent = useSetAtom(selectedMarketplaceEventAtom);
  const setEventMarketplaceDefaults = useSetAtom(eventMarketplaceDefaultsAtom);
  const [rule, setRule] = useAtom(ruleStateAtom);
  const [showEventMapping, setShowEventMapping] = useAtom(showEventMappingAtom);
  const { isLoading: isRuleLoading, resetForm, form, submit, isLoadingLive: isRuleLoadingLive } = useRuleState('isLoading', 'resetForm', 'form', 'submit', 'isLoadingLive');
  const [isEditMarketplaceLinksDialogOpen, _editMarketplaceLinksDialogHandler] = useDisclosure(false);
  const [isMarketplaceLinksOpen, { toggle: toggleMarketplaceLinks, close: closeMarketplaceLinks, open: openMarketplaceLinks }] = useDisclosure(false);
  // Disclosure handler is not stable object
  // eslint-disable-next-line react-hooks/exhaustive-deps
  const editMarketplaceLinksDialogHandler = useMemo(() => _editMarketplaceLinksDialogHandler, []);
  const [bypassCache, setBypassCache] = useState(false);
  const [isMarketSelectorOpen, setIsMarketSelectorOpen] = useState(false);
  const isInitialPageLoad = useAtomValue(initialPageLoadAtom);
  const { tenants, isMobile } = useGlobalState('tenants', 'isMobile');
  const hideSectionSuggestionsFlag = useFlag('hide-section-suggestions');
  const selectedTenant = tenants?.find((t) => t.tenantId === selectedTenantId);
  const settings = selectedTenant?.settings?.pricerSettings ?? ({} as BrokerNerdsBarkerCoreModelsAdministrativeTenantPricerSettings);

  const {
    data: eventMappings,
    isFetching: isMappingsLoading,
    refetch: refetchEventMappings,
  } = useGetApiInventoryEventsEventIdMappings(selectedEvent?.eventId!, {
    query: {
      enabled: !!selectedEvent?.eventId,
      staleTime: 5 * 60 * 1000, // 5 Mins
      queryKey: [...getGetApiInventoryEventsEventIdMappingsQueryKey(selectedEvent?.eventId!), selectedEvent?.tenantId],
      select(data) {
        return data.data;
      },
    },
    axios: {
      headers: {
        'x-tenant-id': selectedEvent?.tenantId,
      },
    },
  });

  const selectedMarketplaceId = rule.marketplaceId;

  const mapping = useMemo(() => eventMappings?.find((m) => m.marketplaceId === selectedMarketplaceId), [eventMappings, selectedMarketplaceId]);

  const pasteUrlForm = useForm<{ url: string }>({
    initialValues: {
      url: '',
    },
    validate: {
      url: (value) => (parseEventIdFromUrl(value) === null ? 'Invalid URL' : null),
    },
  });

  const fetchUpdatedSeatingChart = useCallback(() => {
    setBypassCache(true);
  }, []);

  const {
    data: seatingChartSvgDetails,
    isFetching: isSeatingChartFetching,
    isError: isSeatingChartError,
  } = useGetApiMarketplacesMarketplaceIdEventsEventIdSeatingChart(
    selectedMarketplaceId,
    mapping?.marketplaceEventId!,
    {
      darkMode: isDark,
      bypassCache,
    },
    {
      query: {
        ...(isMobile && { staleTime: 5 * 60 * 1000 }), // 5 Mins
        enabled: !!(selectedEvent?.tenantId && selectedMarketplaceId && mapping?.marketplaceEventId),
      },
      axios: {
        headers: {
          'x-tenant-id': selectedEvent?.tenantId,
        },
      },
    },
  );

  useEffect(() => {
    if (seatingChartSvgDetails) {
      if (bypassCache) {
        // eslint-disable-next-line no-param-reassign
        if (seatingChartSvgDetails.data.chartSvgUrl !== null) {
          seatingChartSvgDetails.data.chartSvgUrl = `${seatingChartSvgDetails.data.chartSvgUrl}?${Math.random().toString(36).substring(7)}`;
        }

        queryClient.setQueryData(
          [
            ...getGetApiMarketplacesMarketplaceIdEventsEventIdSeatingChartQueryKey(selectedMarketplaceId, mapping?.marketplaceEventId!, {
              darkMode: isDark,
              bypassCache: false,
            }),
          ],
          seatingChartSvgDetails,
        );

        setBypassCache(false);
      }
    }
  }, [seatingChartSvgDetails, bypassCache, isDark, mapping?.marketplaceEventId, selectedMarketplaceId]);

  const { data: marketplaceEventDetails, isFetching: isEventLoading } = useGetApiMarketplacesMarketplaceIdEventsEventId(selectedMarketplaceId, mapping?.marketplaceEventId!, {
    query: {
      enabled: !isMappingsLoading && !!mapping?.marketplaceEventId,
      staleTime: 5 * 60 * 1000, // 5 Mins
      select(data) {
        return data.data;
      },
    },
    axios: {
      headers: {
        'x-tenant-id': selectedEvent?.tenantId,
      },
    },
  });

  useDidUpdate(() => {
    setIsBoosted(marketplaceEventDetails?.isAutoPriceBoosted ?? false);
  }, [marketplaceEventDetails?.isAutoPriceBoosted]);

  const filterSectionsToUniqueGroupCount = useMemo(() => {
    const categories = seatingChartSvgDetails?.data?.categories;
    const seatingChartSections = seatingChartSvgDetails?.data.sections || [];
    if (selectedMarketplaceId === BrokerNerdsBarkerCoreEnumsMarketplace.Ticketmaster && seatingChartSections.length > 0) {
      // Precompute each section's lowercased member set once (preserving order so `.find` first-match
      // semantics are kept) instead of rebuilding `y.sections.map(toLowerCase)` for every sectionId.
      const sectionsWithLowerMembers = seatingChartSections.map((y) => ({ name: y.name, members: new Set(y.sections?.map((z) => z.toLowerCase())) }));
      const uniqueSections =
        form.values.sectionIds?.map((x) => sectionsWithLowerMembers.find((y) => y.members.has(x.toLowerCase()))?.name).filter((x) => x) || [];
      return new Set(uniqueSections).size;
    }

    let sectionsSet: Set<string> | undefined;
    if (form.values.sectionIds) {
      sectionsSet = new Set(form.values.sectionIds);
    }

    if (form.values.sectionRows) {
      sectionsSet = new Set<string>(form.values.sectionRows?.filter((x) => x.sectionId).map((x) => x.sectionId));
    }

    if (categories?.length && sectionsSet?.size) {
      categories.forEach((category) => {
        sectionsSet.delete(category.name.toLowerCase().replaceAll('-', ' '));
      });
    }

    return sectionsSet?.size ?? 0;
  }, [form.values.sectionIds, form.values.sectionRows, seatingChartSvgDetails?.data?.categories, seatingChartSvgDetails?.data?.sections, selectedMarketplaceId]);

  const selectedMarketplaceEvent = useMemo(
    () => ({
      marketplaceId: selectedMarketplaceId,
      marketplaceEventId: mapping?.marketplaceEventId,
      marketplaceEvent: marketplaceEventDetails,
      svgDetails: seatingChartSvgDetails?.data,
      isUserMapped: mapping?.isUserMapped || false,
      sections: seatingChartSvgDetails?.data.sections || [],
      isLoading: isSeatingChartFetching || isMappingsLoading || isEventLoading,
    }),
    [
      selectedMarketplaceId,
      mapping?.marketplaceEventId,
      mapping?.isUserMapped,
      marketplaceEventDetails,
      seatingChartSvgDetails?.data,
      isSeatingChartFetching,
      isMappingsLoading,
      isEventLoading,
    ],
  );

  useEffectDeep(() => {
    setSelectedMarketplaceEvent(selectedMarketplaceEvent);
  }, [selectedMarketplaceEvent, setSelectedMarketplaceEvent]);

  const { data: marketplaceListingResult, isFetching: isMarketplaceListingsLoading } = useGetApiMarketplacesMarketplaceIdEventsEventIdListings(
    selectedMarketplaceId,
    mapping?.marketplaceEventId!,
    {
      bypassCache: false,
    },
    {
      query: {
        ...(isMobile && { staleTime: 5 * 60 * 1000 }), // 5 Mins
        enabled: !!(selectedEvent?.tenantId && selectedMarketplaceId && mapping?.marketplaceEventId),
        queryKey: [
          ...getGetApiMarketplacesMarketplaceIdEventsEventIdListingsQueryKey(selectedMarketplaceId, mapping?.marketplaceEventId!, { bypassCache: false }),
          selectedEvent?.tenantId,
        ],
      },
      axios: {
        headers: {
          'x-tenant-id': selectedEvent?.tenantId,
        },
      },
    },
  );

  const { data: sectionAliases } = useGetApiMarketplacesMarketplaceIdEventsEventIdSectionAliases(
    selectedMarketplaceId,
    mapping?.marketplaceEventId!,
    {
      section: selectedSection,
    },
    {
      query: {
        // Used for local testing. Remove when feature is done
        // ****************************
        // enabled: false,
        // initialData: {
        //   data: ['A'],
        // } as AxiosResponse,
        // ****************************
        ...(isMobile && { staleTime: 5 * 60 * 1000 }), // 5 Mins
        enabled: !isMarketplaceListingsLoading && !!mapping?.marketplaceEventId && !!selectedSection,
        select(data) {
          return data.data;
        },
      },
      axios: {
        headers: {
          'x-tenant-id': selectedEvent?.tenantId,
        },
      },
    },
  );

  const { data: suggestionData } = useGetApiPricingSuggestions(
    {
      marketplaceId: rule?.marketplaceId,
      section: selectedSection,
      venueId: selectedEvent?.venue?.venueId,
    },
    {
      query: {
        queryKey: [
          ...getGetApiPricingSuggestionsQueryKey({
            marketplaceId: rule?.marketplaceId,
            section: selectedSection,
            venueId: selectedEvent?.venue?.venueId,
          }),
          selectedEvent?.tenantId,
        ],
        enabled:
          !hideSectionSuggestionsFlag &&
          !isRuleLoadingLive &&
          !isRuleLoading &&
          !!selectedSection &&
          !!selectedEvent?.venue?.venueId &&
          rule?.filters?.sectionIds?.length === 0 &&
          !(settings?.disableSuggestions ?? false),
      },
      axios: {
        headers: {
          'x-tenant-id': selectedEvent?.tenantId,
        },
      },
    },
  );

  // Hack: State sync issue with suggestions. Need to clear them till rule is loaded and next render gets the value.
  const [suggestions, setSuggestions] = useState<{ sectionIds: string[] }>({ sectionIds: [] });
  const setSectionSuggestions = useSetAtom(sectionSuggestionsAtom);
  useEffect(() => {
    if (isRuleLoading || isRuleLoadingLive) {
      setSuggestions({ sectionIds: [] });
    } else {
      setSuggestions({ sectionIds: suggestionData?.data?.sectionIds || [] });
    }
    setSectionSuggestions({ tenantIdListingId: selectedTenantListingId, suggestedSections: suggestionData?.data?.sectionIds || [] });
  }, [suggestionData?.data, isRuleLoading, isRuleLoadingLive, setSectionSuggestions, selectedTenantListingId]);

  const [allSectionsInSeatingChart, updateAllSectionsInSeatingChart] = useState<string[]>([]);

  const marketSectionsWithCounts = useMemo(
    () =>
      R.pipe(
        marketplaceListingResult?.data?.listings || [],
        R.reduce((acc, { section, sectionId, quantity }) => {
          const resolvedSectionId = sectionId || section.toLowerCase();
          // If the section is not in acc, initialize it with default values.
          if (!acc[resolvedSectionId]) {
            acc[resolvedSectionId] = { sectionId: resolvedSectionId, section, count: 0, totalQuantity: 0 };
          }
          // Increment the count and totalQuantity for the section.
          acc[resolvedSectionId].count += 1;
          acc[resolvedSectionId].totalQuantity += quantity;

          return acc;
        }, {} as RecordSectionCounts),
        R.values, // Convert the object to an array of values.
        R.uniqBy((x) => x.sectionId || x.section), // Remove duplicates.
        R.sort((a, b) => a.section.localeCompare(b.section, undefined, { numeric: true, sensitivity: 'base' })),
      ),
    [marketplaceListingResult?.data?.listings],
  );

  const sectionsWithListings = useMemo(() => marketSectionsWithCounts.map((x) => x.sectionId), [marketSectionsWithCounts]);

  const allSectionsInSeatingChartWithCounts = useMemo(() => {
    // Precompute a lowercase-section -> entry Map (first occurrence wins, matching the
    // prior `.find` semantics) to replace the O(n*m) `.find` inside `.map` below with O(1) lookups.
    const marketSectionsByLowerSection = new Map<string, SectionCounts>();
    for (const y of marketSectionsWithCounts) {
      const key = y.section.toLowerCase();
      if (!marketSectionsByLowerSection.has(key)) {
        marketSectionsByLowerSection.set(key, y);
      }
    }
    return allSectionsInSeatingChart
      .flatMap((x) => {
        if (selectedMarketplaceId === BrokerNerdsBarkerCoreEnumsMarketplace.Ticketmaster) {
          return x.split(',') ?? [];
        }
        return x;
      })
      .map((x) => {
        const found = marketSectionsByLowerSection.get(x.toLowerCase());
        const count = found ? found.count : 0;
        const totalQuantity = found ? found.totalQuantity : 0;
        return {
          // TicketMaster uses uppercase for section ids
          sectionId: selectedMarketplaceId === BrokerNerdsBarkerCoreEnumsMarketplace.Ticketmaster ? x.toUpperCase() : x.toLowerCase(),
          section: selectedMarketplaceEvent.svgDetails?.categories?.length ? x.slice(x.indexOf('_') + 1) : x,
          count,
          totalQuantity,
        } as SectionCounts;
      })
      .sort((a, b) => a.section.localeCompare(b.section, undefined, { numeric: true, sensitivity: 'base' }));
  }, [allSectionsInSeatingChart, marketSectionsWithCounts, selectedMarketplaceId, selectedMarketplaceEvent.svgDetails?.categories?.length]);

  const ruleSectionsMissingCount = useMemo(() => {
    const knownSeatingChartSections = new Set(allSectionsInSeatingChart.map((x) => x.toLowerCase()));
    return (rule.filters?.sectionIds?.filter((x) => !knownSeatingChartSections.has(x.toLowerCase())) ?? []).length;
  }, [allSectionsInSeatingChart, rule.filters?.sectionIds]);

  const allMissingSections = useMemo(() => {
    const sections = new Set(allSectionsInSeatingChart.map((x) => x.toLowerCase()));
    const missingMarketSections = marketSectionsWithCounts.filter((x) => !sections.has(x.section.toLowerCase()));
    const missingRuleSections = rule.filters?.sectionIds?.filter((x) => !sections.has(x.toLowerCase())) ?? [];
    return R.uniqBy(
      [
        ...missingMarketSections,
        ...missingRuleSections.map((x) => ({
          sectionId: x.toLowerCase(),
          section: selectedMarketplaceEvent.svgDetails?.categories?.length ? x.slice(x.indexOf('_') + 1) : x.replaceAll('-', ' '),
          count: 0,
          totalQuantity: 0,
        })),
      ] satisfies SectionCounts[],
      (x) => x.section,
    ).sort((a, b) => a.section.localeCompare(b.section, undefined, { numeric: true, sensitivity: 'base' }));
  }, [allSectionsInSeatingChart, marketSectionsWithCounts, rule.filters?.sectionIds, selectedMarketplaceEvent.svgDetails?.categories?.length]);

  const seatGeekParkingSectionsFiltered = useMemo(() => {
    const seatGeekParkingSections =
      selectedMarketplaceEvent?.marketplaceId !== 'SeatGeek' ? [] : selectedMarketplaceEvent.svgDetails?.zones?.filter((z) => z.name === 'Parking')?.[0]?.sections || [];

    // Precompute the set of existing sectionIds so the membership check below is O(1) instead of
    // `.some` scanning the full array per parking section (O(n*m) -> O(n+m)).
    const sectionIdsInSeatingChart = new Set(allSectionsInSeatingChartWithCounts.map((a) => a.sectionId));
    return seatGeekParkingSections
      .filter((s) => !sectionIdsInSeatingChart.has(s))
      .map((s) => ({
        sectionId: s,
        section: s.replaceAll('-', ' '),
        count: 0,
        totalQuantity: 0,
      })) as SectionCounts[];
  }, [allSectionsInSeatingChartWithCounts, selectedMarketplaceEvent?.marketplaceId, selectedMarketplaceEvent.svgDetails?.zones]);

  const allSectionsWithCounts = useMemo(
    () =>
      R.uniqBy([...allSectionsInSeatingChartWithCounts, ...allMissingSections, ...seatGeekParkingSectionsFiltered], (x) => x.sectionId || x.section).sort((a, b) =>
        a.sectionId.localeCompare(b.sectionId, undefined, { numeric: true, sensitivity: 'base' }),
      ),
    [allMissingSections, allSectionsInSeatingChartWithCounts, seatGeekParkingSectionsFiltered],
  );

  useDidUpdate(() => {
    updateAllSectionsInSeatingChart([]);
  }, [selectedMarketplaceEvent?.marketplaceEventId]);

  const [showUnmapped, setShowUnmapped] = useState(false);
  const [filter, setFilter] = useState<string | undefined>();
  const [filterQuery, setFilterQuery] = useState('');

  useDidUpdate(() => {
    setFilter('');
    setFilterQuery('');
    setShowUnmapped(false);
    updateAllSectionsInSeatingChart([]);
    if (showEventMapping && !isSeasonView) {
      setShowEventMapping(false);
    }
  }, [selectedEvent?.eventId]);

  const filteredSections = useMemo(() => {
    let matchingSections = [...allSectionsWithCounts];
    if (!filterQuery) {
      return matchingSections.sort((a, b) =>
        a.section.localeCompare(b.section, undefined, {
          numeric: true,
          sensitivity: 'base',
        }),
      );
    }

    const andParts = filterQuery.split(/\s+/);

    for (const andPart of andParts) {
      const andSections: SectionCounts[] = [];
      const orParts = andPart.split(',');

      for (const orPart of orParts) {
        const rangeParts = orPart.split('-');

        if (
          rangeParts.length === 2 &&
          ((!isNaN(Number(rangeParts[0])) && !isNaN(Number(rangeParts[1]))) ||
            ([...rangeParts[0]].every((char) => char === rangeParts[0][0]) && [...rangeParts[1]].every((char) => char === rangeParts[1][0])))
        ) {
          // Number range
          andSections.push(...matchingSections.filter((section) => matchesRowFilter(section.section, [orPart])));
        } else if (orPart.length <= 3 && (!isNaN(Number(orPart)) || [...orPart].every((char) => char === orPart[0]))) {
          andSections.push(...matchingSections.filter((section) => matchesRowFilter(section.section, [orPart])));
        } else {
          // Text match
          andSections.push(...matchingSections.filter((section) => section.section.toLowerCase().includes(orPart.toLowerCase())));
        }
      }

      matchingSections = andSections;
    }

    return matchingSections.sort((a, b) =>
      a.section.localeCompare(b.section, undefined, {
        numeric: true,
        sensitivity: 'base',
      }),
    );
  }, [allSectionsWithCounts, filterQuery]);

  const resetAll = useCallback(() => {
    resetForm();
  }, [resetForm]);

  const advancedSectionRows = useMemo(() => {
    const advancedRows = form.values.sectionRows?.reduce((acc, item) => {
      if (!acc.has(item.rows)) {
        acc.set(item.rows, []);
      }
      acc.get(item.rows)!.push(item.sectionId);
      return acc;
    }, new Map<string, string[]>());

    return {
      isEnabled: advancedRows !== undefined,
      advancedRows,
      isEmpty: !advancedRows || [...(form.values.sectionRows?.values() ?? [])]?.some((x) => x.rows === ''),
      hasSections: [...(form.values.sectionRows?.values() ?? [])]?.some((x) => x.sectionId !== ''),
    };
  }, [form.values.sectionRows]);

  const suggestedSectionsInSeatingChart = useMemo(() => {
    // Solves race condition when selected listing ruleId and rule hasn't been set yet from server.
    if ((selectedListingRuleId && selectedListingRuleId !== rule.ruleId) || (rule.filters?.sectionIds?.length ?? 0) > 0 || advancedSectionRows.hasSections) {
      return { sectionIds: [] };
    }
    const _suggestions = structuredClone(suggestions) || { sectionIds: [], ruleId: rule.ruleId };
    if (allSectionsInSeatingChart.length === 0 || !_suggestions.sectionIds) {
      return suggestions || { sectionIds: [], ruleId: rule.ruleId };
    }
    _suggestions.sectionIds = _suggestions.sectionIds?.filter((x) => allSectionsInSeatingChart.includes(x)) || [];
    return _suggestions;
  }, [advancedSectionRows.hasSections, allSectionsInSeatingChart, rule.filters?.sectionIds?.length, rule.ruleId, selectedListingRuleId, suggestions]);

  const isLoading = useMemo(
    () => isSeatingChartFetching || isMarketplaceListingsLoading || isMappingsLoading || isEventLoading || isRuleLoading || isInitialPageLoad,
    [isSeatingChartFetching, isMarketplaceListingsLoading, isMappingsLoading, isEventLoading, isRuleLoading, isInitialPageLoad],
  );

  const [forceSectionCheckList, toggleForceSectionChecklist] = useToggle();
  const [isBoosted, setIsBoosted] = useState(marketplaceEventDetails?.isAutoPriceBoosted ?? false);
  const { isSuccess: isBoostingAllowed } = useGetApiMarketplacesMarketplaceIdBoostsEnabled(selectedMarketplaceId, {
    axios: {
      headers: {
        'x-tenant-id': selectedEvent?.tenantId,
      },
    },
    query: {
      ...(isMobile && { staleTime: 5 * 60 * 1000 }), // 5 Mins
      queryKey: [...getGetApiMarketplacesMarketplaceIdBoostsEnabledQueryKey(selectedMarketplaceId), selectedEvent?.tenantId],
      enabled: !!selectedMarketplaceId && !!selectedEvent?.tenantId && selectedMarketplaceId === BrokerNerdsBarkerCoreEnumsMarketplace.SeatGeek,
    },
  });
  const boostConfirm = useCallback(() => {
    openConfirmModal({
      title: 'Confirm Event Boost',
      children: <Text size="sm">You can only boost a single event. This will replace any other events that are currently boosted.</Text>,
      labels: { confirm: 'Continue', cancel: 'Cancel' },
      confirmProps: { className: 'confirmButton', variant: 'filled', color: 'gray', size: 'sm' },
      cancelProps: { className: 'cancelButton', variant: 'default', size: 'sm' },
      closeButtonProps: { size: 'md' },
      async onConfirm() {
        await postApiMarketplacesMarketplaceIdEventsEventIdBoost(selectedMarketplaceId, mapping?.marketplaceEventId!, {
          headers: {
            'x-tenant-id': selectedEvent?.tenantId,
          },
        });
        setIsBoosted(true);
      },
    });
  }, [mapping?.marketplaceEventId, selectedEvent?.tenantId, selectedMarketplaceId]);

  const removeBoost = useCallback(() => {
    openConfirmModal({
      title: 'Stop Event Boost',
      children: <Text size="sm">This will stop boosting this event. Note you can only have one event boosted at a time.</Text>,
      labels: { confirm: 'Continue', cancel: 'Cancel' },
      confirmProps: { className: 'confirmButton', variant: 'filled', color: 'gray', size: 'sm' },
      cancelProps: { className: 'cancelButton', variant: 'default', size: 'sm' },
      closeButtonProps: { size: 'md' },
      onConfirm() {
        setIsBoosted(false);
      },
    });
  }, []);

  const availableMarketplaces = useMemo(() => {
    if (!selectedEvent) {
      return [];
    }

    if (!tenants) {
      return [];
    }

    const tenant = tenants.find((t) => t.tenantId === selectedEvent.tenantId);
    if (!tenant) {
      return [];
    }

    return tenant.marketplaceIds;
  }, [selectedEvent, tenants]);

  const [advancedRowsActiveIndex, setAdvancedRowsActiveIndex] = useState(0);

  useEffect(() => {
    if (advancedRowsActiveIndex >= (advancedSectionRows.advancedRows?.size ?? 0)) {
      setAdvancedRowsActiveIndex((advancedSectionRows?.advancedRows?.size || 0) - 1);
    }
  }, [advancedRowsActiveIndex, advancedSectionRows.advancedRows?.size]);

  const uniqueCount = (arr: any[], key: string) => new Set(arr.map((o) => o[key])).size;

  const selectAllSections = useCallback(() => {
    const allSectionsSet = new Set<string>(allSectionsInSeatingChart);
    if (!advancedSectionRows.isEnabled) {
      setRule((x) => ({ ...x, filters: { ...x.filters, sectionIds: Array.from(allSectionsSet), sectionRows: undefined } }));
    } else {
      const selectedAdvancedRowGroup = advancedSectionRows.advancedRows && [...advancedSectionRows.advancedRows.keys()][advancedRowsActiveIndex];
      if (selectedAdvancedRowGroup) {
        const sections = allSectionsInSeatingChart.map(
          (x) =>
            ({
              rows: selectedAdvancedRowGroup,
              sectionId: x,
            }) satisfies BrokerNerdsBarkerCoreModelsPricingSectionRowFilter,
        );
        setRule((x) => ({ ...x, filters: { ...x.filters, rows: undefined, sectionIds: [], sectionRows: sections } }));
      }
    }
  }, [advancedRowsActiveIndex, advancedSectionRows.advancedRows, advancedSectionRows.isEnabled, allSectionsInSeatingChart, setRule]);

  const handleAdvancedRowSectionAdd = (val: string) => {
    if (form.values.rows) {
      form.setFieldValue('rows', undefined);
    }
    if (form.values.sectionIds && form.values.sectionIds.length > 0) {
      form.setFieldValue('sectionIds', undefined);
      form.setFieldValue(
        'sectionRows',
        form.values.sectionIds.map((sectionId) => ({ sectionId, rows: val })),
      );
    } else if (form.values.sectionRows?.some((sectionRow) => sectionRow.sectionId === '' && sectionRow.rows === '')) {
      form.setFieldValue('sectionRows', [
        {
          sectionId: '',
          rows: val,
        } satisfies BrokerNerdsBarkerCoreModelsPricingSectionRowFilter,
      ]);
    } else if (!form.values.sectionRows?.some((sectionRow) => sectionRow.rows === val)) {
      const newSectionRows = [
        ...(form.values.sectionRows || []),
        {
          sectionId: '',
          rows: val,
        },
      ] satisfies BrokerNerdsBarkerCoreModelsPricingSectionRowFilter[];
      if (newSectionRows.some((sectionRow) => sectionRow.rows === '')) {
        newSectionRows.forEach((sectionRow) => {
          if (sectionRow.rows === '') {
            // eslint-disable-next-line no-param-reassign
            sectionRow.rows = val;
          }
        });
      }
      const seen = new Set();
      const uniqueEntries = newSectionRows.filter((item) => {
        const key = `${item.sectionId}|${item.rows}`;
        if (seen.has(key)) {
          return false;
        }
        seen.add(key);
        return true;
      });
      form.setFieldValue('sectionRows', uniqueEntries);
      setAdvancedRowsActiveIndex(advancedSectionRows.advancedRows ? uniqueCount(uniqueEntries, 'rows') - 1 : 0);
    }
  };

  const handleAdvancedRowSectionRemove = (val: string) => {
    if (advancedRowsActiveIndex >= 0) {
      setAdvancedRowsActiveIndex(advancedRowsActiveIndex - 1);
    }

    form.setFieldValue(
      'sectionRows',
      (form.values.sectionRows || []).filter((v) => v.rows !== val),
    );
  };

  const handleAdvancedRowSectionUpdate = (oldValue: string, newValue: string) => {
    const updated = (form.getValues().sectionRows || []).map((item) => {
      const updatedItem = item.rows === oldValue ? { ...item, rows: newValue } : item;
      return updatedItem;
    });

    const reducedRows = updated.reduce((acc, item) => {
      if (!acc.has(item.rows)) {
        acc.set(item.rows, []);
      }
      acc.get(item.rows)!.push(item.sectionId);
      return acc;
    }, new Map<string, string[]>());

    const reducedRowsArray = [...reducedRows.entries()].flatMap(([rows, sectionIds]) =>
      sectionIds.filter((x) => (sectionIds.length > 1 ? x : true)).map((sectionId) => ({ sectionId, rows })),
    );

    form.setFieldValue('sectionRows', reducedRowsArray);
  };

  const onRowChange = (val?: string) => form.setFieldValue('rows', val || undefined);

  const advancedRowsActiveIndexRef = useRef(advancedRowsActiveIndex);
  useEffect(() => {
    advancedRowsActiveIndexRef.current = advancedRowsActiveIndex;
  }, [advancedSectionRows.advancedRows, advancedRowsActiveIndex]);

  const onSectionClickAdvanced = (_sectionIds: string[] | string) => {
    const currentAdvancedRows =
      // Note: This is needed due to onSectionClick being wired up to SVG click events manually.
      form.getValues().sectionRows?.reduce(
        (acc, item) => {
          if (!acc[item.rows]) {
            acc[item.rows] = [];
          }
          acc[item.rows].push(item.sectionId);
          return acc;
        },
        {} as Record<string, string[]>,
      ) || {};

    const currentAdvancedRowsActiveIndex = advancedRowsActiveIndexRef.current ?? 0;
    const selectedAdvancedRowGroup = currentAdvancedRows && Object.keys(currentAdvancedRows || {})[currentAdvancedRowsActiveIndex];
    let advancedRowGroupSections = currentAdvancedRows[selectedAdvancedRowGroup] ?? [];
    const currentSectionIds = Object.values(currentAdvancedRows).flat();

    if (Array.isArray(_sectionIds)) {
      const hasAny = _sectionIds.some((x) => advancedRowGroupSections?.includes(x));
      const hasAll = _sectionIds.every((x) => advancedRowGroupSections?.includes(x));

      if (!hasAll) {
        _sectionIds.forEach((section) => {
          if (currentSectionIds.includes(section)) {
            // Find sectionInd currentAdvancedRows and remove it
            Object.keys(currentAdvancedRows).forEach((rows) => {
              currentAdvancedRows[rows] = currentAdvancedRows[rows].filter((x) => x !== section);
            });
          }
          if (!advancedRowGroupSections?.includes(section)) {
            advancedRowGroupSections = [...(advancedRowGroupSections.filter((x) => x !== '') || []), section];
          } else if (!hasAny) {
            advancedRowGroupSections = advancedRowGroupSections.filter((x) => x !== section) || [];
          }
        });
      } else {
        advancedRowGroupSections = advancedRowGroupSections.filter((x) => !_sectionIds.includes(x)) || [];
      }
    } else if (!advancedRowGroupSections?.includes(_sectionIds)) {
      advancedRowGroupSections = [...(advancedRowGroupSections.filter((x) => x !== '') || []), _sectionIds];
    } else {
      advancedRowGroupSections = advancedRowGroupSections.filter((x) => x !== _sectionIds) || [];
    }
    currentAdvancedRows[selectedAdvancedRowGroup || ''] = advancedRowGroupSections;
    form.setFieldValue('rows', undefined);
    form.setFieldValue(
      'sectionRows',
      Object.keys(currentAdvancedRows).flatMap((rows) =>
        (currentAdvancedRows[rows].length ? currentAdvancedRows[rows] : ['']).map((sectionId) => ({
          rows,
          sectionId,
        })),
      ),
    );
  };

  const onSectionClick = (_sectionIds: string[] | string) => {
    // Note: This is needed due to onSectionClick being wired up to SVG click events manually.
    if (form.getValues().sectionRows) {
      onSectionClickAdvanced(_sectionIds);
      return;
    }
    let clickedSectionIds: string[] = [..._sectionIds];
    const formSectionSet = new Set(form.getValues().sectionIds || []);
    const sectionIdSet = new Set(Array.isArray(_sectionIds) ? _sectionIds : [_sectionIds]);
    const hasAll = clickedSectionIds.every((x) => formSectionSet.has(x));

    if (selectedMarketplaceEvent.svgDetails?.categories?.length) {
      for (const cat of selectedMarketplaceEvent.svgDetails.categories) {
        const normalizedCatId = cat.sectionId.toLowerCase().replaceAll('-', ' ');
        const normalizedCatSectionIds =
          cat.sections?.reduce<string[]>((acc, s) => {
            const normalizedSectionId = s.toLowerCase().replaceAll('-', ' ');
            // Sometimes categories contain themselves listed in their sections
            if (normalizedSectionId !== normalizedCatId) {
              acc.push(`${normalizedCatId}_${normalizedSectionId}`);
            }
            return acc;
          }, []) ?? [];

        const hasCategoryMatch = sectionIdSet.has(normalizedCatId);

        if (hasCategoryMatch) {
          // category clicked, click all sections as well
          clickedSectionIds.push(normalizedCatId, ...normalizedCatSectionIds);
        } else if (formSectionSet.has(normalizedCatId) && normalizedCatSectionIds.some((s) => sectionIdSet.has(s))) {
          // category selected but section clicked, so click category as well
          clickedSectionIds.push(normalizedCatId);
        }
      }
    }

    // deduplicate because we probably doubled up some sections
    clickedSectionIds = [...new Set(clickedSectionIds)];

    if (!hasAll) {
      clickedSectionIds.forEach((section) => {
        if (section) formSectionSet.add(section);
      });
    } else {
      clickedSectionIds.forEach((section) => {
        formSectionSet.delete(section);
      });
    }

    form.setFieldValue('sectionIds', Array.from(formSectionSet));
    form.setFieldValue('sectionRows', null);
  };

  // onSectionClick is recreated every render; mirror it into a ref so acceptAllSuggestions
  // stays stable without re-subscribing the document listener on each render.
  const onSectionClickRef = useRef(onSectionClick);
  onSectionClickRef.current = onSectionClick;

  const acceptAllSuggestions = useCallback(() => {
    if (suggestedSectionsInSeatingChart.sectionIds.length === 0) {
      return;
    }
    onSectionClickRef.current(suggestedSectionsInSeatingChart.sectionIds);
  }, [suggestedSectionsInSeatingChart.sectionIds]);

  useEffect(() => {
    document.addEventListener(onAcceptSuggestions.type, acceptAllSuggestions);

    return () => {
      document.removeEventListener(onAcceptSuggestions.type, acceptAllSuggestions);
    };
  }, [acceptAllSuggestions]);

  return {
    isLoading,
    selectAllSections,
    isSeatingChartError,
    resetAll,
    selectedMarketplaceEvent,
    sectionsWithListings,
    rule,
    eventMappings,
    showEventMapping,
    setShowEventMapping,
    refetchEventMappings,
    selectedMarketplaceId,
    selectedListingRuleId,
    setSelectedMarketplaceId,
    fetchUpdatedSeatingChart,
    isEditMarketplaceLinksDialogOpen,
    editMarketplaceLinksDialogHandler,
    forceSectionCheckList,
    toggleForceSectionChecklist,
    filterSectionsToUniqueGroupCount,
    updateAllSectionsInSeatingChart,
    allSectionsInSeatingChart,
    suggestions: suggestedSectionsInSeatingChart,
    setEventMarketplaceDefaults,
    setRule,
    marketSectionsWithCounts,
    filter,
    setFilter,
    filterQuery,
    setFilterQuery,
    filteredSections,
    allMissingSections,
    allSectionsInSeatingChartWithCounts,
    allSectionsWithCounts,
    ruleSectionsMissingCount,
    boostConfirm,
    removeBoost,
    isBoosted,
    isBoostingAllowed,
    showUnmapped,
    setShowUnmapped,
    selectedTenantId,
    selectedSection,
    selectedEvent,
    sectionAliases,
    isSeasonView,
    form,
    onRowChange,
    submit,
    availableMarketplaces,
    selectedTenantListingId,
    advancedRowsActiveIndex,
    setAdvancedRowsActiveIndex,
    advancedSectionRows,
    handleAdvancedRowSectionAdd,
    handleAdvancedRowSectionRemove,
    handleAdvancedRowSectionUpdate,
    onSectionClick,
    onSectionClickAdvanced,
    setIsMarketSelectorOpen,
    isMarketSelectorOpen,
    isMarketplaceLinksOpen,
    toggleMarketplaceLinks,
    closeMarketplaceLinks,
    openMarketplaceLinks,
    pasteUrlForm,
  };
}

export const { Provider: SeatingChartProvider, useSelector: useSeatingChart } = yasml(SeatingChartState);
